import {v4 as uuidv4} from 'uuid';

import {getIncompleteRetryDelayMs} from '../../../util/apolloRetryAfter';
import {type RequestCause} from '../../../util/apolloRetryHeader';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {bucketedQuery} from '../bucketedData/bucketedDeltaQuery';
import {type BucketedQueryType} from '../bucketedData/queries/queryVars';
import {
  type RequestKeys,
  requestKeys,
} from '../bucketedData/queries/requestKey';
import {bracketHistoriesData} from '../bucketedData/util';
import {
  type AttemptOutcome,
  FIRST_ATTEMPT,
  type LadderEvent,
  type LadderState,
  nextAttempt,
  type NextStep,
} from './followUpLadder';
import {findPlaceholder, isZoomContained} from './placeholders';
import {
  type BucketedData,
  type QueryStore,
  type QueryStoreListener,
  type QueryStoreRequest,
  type QueryStoreState,
} from './queryStore';

/** Fetches one request, taking bucketedQuery's arguments; tests pass a fake. */
export type QueryStoreQueryFn = (
  args: Parameters<typeof bucketedQuery>[0]
) => Promise<BucketedData>;

/** A request is refetched on poll once its data is older than this. */
const POLL_STALE_MS = 10_000;

/**
 * One initial delta query, its follow-ups, and Retry-After waits. The
 * operation holds cancellation and attempt metadata; results stay in Entry.
 * Only the entry's current operation may update it, and its controller's
 * signal goes with every request it sends.
 */
interface DeltaOperation {
  /** The attempt in flight or waiting; 0 is the first fetch. */
  attempt: number;
  controller: AbortController;
  startedAt: number;
  traceId: string;
}

type Status =
  /** A delta operation is fetching. */
  | {kind: 'loading'; deltaOperation: DeltaOperation}
  /** No active operation; the data is as complete as the last operation left it. */
  | {kind: 'idle'}
  /** A delta operation was stopped before it finished, so the data may be partial. */
  | {kind: 'stopped'};

/** One distinct request: its subscriptions, its data, and its fetch. */
interface Entry {
  keys: RequestKeys;
  /** The request the entry was created with. It never changes. */
  request: QueryStoreRequest;
  subscriptions: Map<symbol, QueryStoreListener>;
  status: Status;
  /** The entry's own data, and the delta baseline for its fetches. */
  data: BucketedData | null;
  error: Error | null;
  lastUpdated: number;
  /**
   * Another request's data, chosen when the entry was created. It is shown
   * until the entry has data of its own, then brackets that data. It is
   * never the delta baseline.
   */
  placeholder: BucketedData | null;
  /** Orders releases; the highest is the most recently released. */
  releaseOrder: number;
}

/**
 * The members of one zoom family worth keeping. Zoom only narrows step by
 * step or resets to the full range, so two levels can be needed again: the
 * widest, shown at once when the user zooms back out, and the most recent.
 * While a member has subscribers it is the most recent. Once none does
 * (after a run toggle, say), the last one released is kept so toggling back
 * is a cache hit until the next poll.
 */
function keptZoomLevels(family: Entry[], hasSubscribers: boolean): Set<Entry> {
  let widest: Entry | undefined;
  let latest: Entry | undefined;
  for (const candidate of family) {
    if (candidate.data === null) {
      continue;
    }
    if (!hasSubscribers) {
      latest =
        latest && latest.releaseOrder > candidate.releaseOrder
          ? latest
          : candidate;
    }
    if (widest !== undefined) {
      continue;
    }

    let contained = false;
    for (const other of family) {
      if (
        other !== candidate &&
        other.data !== null &&
        isZoomContained(
          candidate.request.runsDataQuery,
          other.request.runsDataQuery
        )
      ) {
        contained = true;
        break;
      }
    }
    if (!contained) {
      widest = candidate;
    }
  }

  const kept = new Set<Entry>();
  if (widest !== undefined) {
    kept.add(widest);
  }
  if (latest !== undefined) {
    kept.add(latest);
  }
  return kept;
}

interface CancelledEvent {
  attempt: number;
  /**
   * Time since the delta operation started. This is client time; the server
   * may keep working on an aborted request.
   */
  elapsedMs: number;
  incompleteCount: number;
  phase: 'cancelled';
}

type FetchLifecycleEvent = (LadderEvent | CancelledEvent) & {
  queryType: BucketedQueryType;
  traceId: string;
};

/**
 * Same action name and fields as BucketedQueryManager, so existing queries
 * keep working. `implementation` tells the two apart.
 */
function recordFetchLifecycleEvent(event: FetchLifecycleEvent) {
  recordRumAction('BucketedQueryManager: incomplete_fetch', {
    ...event,
    implementation: 'bucketed-query-source',
  });
}

function followUpCause(
  state: LadderState,
  wasDelayedByRetryAfter: boolean
): RequestCause {
  if (state.errorRetry) {
    return 'error-refetch';
  }
  return wasDelayedByRetryAfter
    ? 'incomplete-refetch-delayed'
    : 'incomplete-refetch';
}

/**
 * Waits out a partial-shed Retry-After, ending early if `signal` aborts.
 * Returns whether the wait was a real hold-back; a zero-second hint only adds
 * jitter.
 */
async function waitForRetryAfter(
  retryAfterSeconds: number | undefined,
  signal: AbortSignal
): Promise<boolean> {
  const ms = getIncompleteRetryDelayMs(retryAfterSeconds);
  if (ms <= 0) {
    return false;
  }
  await new Promise<void>(resolve => {
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done);
  });
  return (retryAfterSeconds ?? 0) > 0;
}

/** What every subscription to `entry` sees. */
function published(entry: Entry): QueryStoreState {
  const {data, error, lastUpdated, placeholder} = entry;
  const loading = entry.status.kind === 'loading';
  // Until the entry has data of its own, the placeholder is shown as it is.
  if (data === null && error === null && placeholder !== null) {
    return {
      data: placeholder,
      error,
      lastUpdated,
      loading,
      lowerFidelityData: null,
    };
  }
  return {data, error, lastUpdated, loading, lowerFidelityData: placeholder};
}

/**
 * Loads bucketed line-plot data for one panel provider, replacing a
 * BucketedQueryManager per runset. Subscriptions to the same request, as
 * requestKeys compares them, share one entry and one fetch. Work is bound
 * to subscriptions: when a request's last one is released, its fetch,
 * follow-ups and waits stop.
 */
export function createQueryStore(
  queryFn: QueryStoreQueryFn = bucketedQuery
): QueryStore {
  const entries = new Map<string, Entry>();
  let releaseCount = 0;

  function publish(entry: Entry) {
    const state = published(entry);
    entry.subscriptions.forEach(listener => listener(state));
  }

  async function fetchAttempt(
    entry: Entry,
    deltaOperation: DeltaOperation,
    timeoutSeconds: number,
    cause: RequestCause | undefined
  ): Promise<BucketedData> {
    const {request} = entry;
    const data = await queryFn({
      currentRunsById: entry.data?.runsById ?? {},
      expressions: request.expressions,
      loggingInfo: request.loggingInfo,
      nBuckets: request.nBuckets,
      queryType: request.queryType,
      runsDataQuery: request.runsDataQuery,
      singleQuery: request.singleQuery,
      timeout: timeoutSeconds,
      cause,
      signal: deltaOperation.controller.signal,
    });
    Object.values(data.runsById).forEach(line => {
      line.runsetId = request.singleQuery.id;
    });
    return data;
  }

  /**
   * Makes the fetches the ladder decides, from the first to the last
   * follow-up. Returns early once this delta operation is no longer current.
   */
  async function fetchWithFollowUps(
    entry: Entry,
    deltaOperation: DeltaOperation,
    cause?: RequestCause
  ) {
    // An aborted request can still settle. Recheck after each await so an
    // older operation cannot publish results or continue its follow-ups.
    const isCurrentDeltaOperation = () =>
      entry.status.kind === 'loading' &&
      entry.status.deltaOperation === deltaOperation;
    let step: NextStep = FIRST_ATTEMPT;
    let retryAfterSeconds: number | undefined;
    while (step.kind === 'fetch') {
      const {mode, state} = step;
      deltaOperation.attempt = state.attempt;
      // Retry-After from a successful attempt holds back the next follow-up.
      const wasDelayedByRetryAfter =
        mode === 'merge' && !state.errorRetry
          ? await waitForRetryAfter(
              retryAfterSeconds,
              deltaOperation.controller.signal
            )
          : false;
      if (!isCurrentDeltaOperation()) {
        return;
      }
      let outcome: AttemptOutcome;
      try {
        const result = await fetchAttempt(
          entry,
          deltaOperation,
          state.timeoutSeconds,
          mode === 'replace'
            ? cause
            : followUpCause(state, wasDelayedByRetryAfter)
        );
        if (!isCurrentDeltaOperation()) {
          return;
        }
        entry.data =
          mode === 'replace'
            ? result
            : {
                ...result,
                runsById: {...entry.data?.runsById, ...result.runsById},
              };
        entry.error = null;
        entry.lastUpdated = Date.now();
        retryAfterSeconds = result.retryAfterSeconds;
        outcome = {incomplete: result.incomplete ?? []};
      } catch (error) {
        if (!isCurrentDeltaOperation()) {
          return;
        }
        const failure =
          error instanceof Error ? error : new Error(String(error));
        // A failed first fetch is the request's error. A failed follow-up
        // keeps the runs already loaded.
        if (mode === 'replace') {
          entry.error = failure;
          entry.lastUpdated = Date.now();
        }
        outcome = {errorMessage: failure.message, failed: true};
      }
      const {events, next} = nextAttempt(state, outcome);
      if (next.kind === 'stop') {
        entry.status = {kind: 'idle'};
      }
      // Publish once per change: new data, or the end of loading.
      if (!('failed' in outcome) || next.kind === 'stop') {
        publish(entry);
      }
      events.forEach(event =>
        recordFetchLifecycleEvent({
          ...event,
          queryType: entry.request.queryType,
          traceId: deltaOperation.traceId,
        })
      );
      step = next;
    }
  }

  function startEntryFetch(entry: Entry, cause?: RequestCause) {
    const deltaOperation: DeltaOperation = {
      attempt: 0,
      controller: new AbortController(),
      startedAt: Date.now(),
      traceId: uuidv4(),
    };
    entry.status = {kind: 'loading', deltaOperation};
    publish(entry);
    void fetchWithFollowUps(entry, deltaOperation, cause);
  }

  /**
   * Aborts the entry's delta operation: its request, follow-ups and waits.
   * Data already loaded stays, and the next subscription refreshes it.
   */
  function abortEntryFetch(entry: Entry) {
    if (entry.status.kind !== 'loading') {
      return;
    }
    const {deltaOperation} = entry.status;
    deltaOperation.controller.abort();
    recordFetchLifecycleEvent({
      attempt: deltaOperation.attempt,
      elapsedMs: Date.now() - deltaOperation.startedAt,
      incompleteCount: entry.data?.incomplete?.length ?? 0,
      phase: 'cancelled',
      queryType: entry.request.queryType,
      traceId: deltaOperation.traceId,
    });
    entry.status = {kind: 'stopped'};
  }

  /**
   * Drops released entries that are not kept zoom levels, stopping their
   * work first. A poll also drops the kept levels of families without
   * subscribers.
   */
  function prune(polling: boolean) {
    const families = new Map<string, Entry[]>();
    entries.forEach(entry => {
      const family = families.get(entry.keys.family);
      if (family) {
        family.push(entry);
      } else {
        families.set(entry.keys.family, [entry]);
      }
    });
    families.forEach(family => {
      const hasSubscribers = family.some(entry => entry.subscriptions.size > 0);
      const kept = keptZoomLevels(family, hasSubscribers);
      family.forEach(entry => {
        const keep =
          entry.subscriptions.size > 0 ||
          (kept.has(entry) && (hasSubscribers || !polling));
        if (!keep) {
          abortEntryFetch(entry);
          entries.delete(entry.keys.exact);
        }
      });
    });
  }

  function release(entry: Entry, token: symbol) {
    if (!entry.subscriptions.delete(token) || entry.subscriptions.size > 0) {
      return;
    }
    entry.releaseOrder = ++releaseCount;
    // A React effect that re-runs releases and resubscribes in the same
    // commit. Waiting one microtask keeps that request's fetch alive.
    queueMicrotask(() => {
      if (entry.subscriptions.size === 0) {
        abortEntryFetch(entry);
        prune(false);
      }
    });
  }

  return {
    subscribe(request, listener) {
      const keys = requestKeys(request);
      let entry = entries.get(keys.exact);
      if (!entry) {
        const settled = [...entries.values()].flatMap(other =>
          other.data !== null && other.status.kind !== 'loading'
            ? [
                {
                  // A narrowed request may itself have borrowed outside points.
                  // Preserve those for the next zoom without changing its delta baseline.
                  data:
                    bracketHistoriesData(
                      published(other),
                      other.request.runsDataQuery.bucketedHistorySpecs?.[0]
                        ?.xAxis ?? '_step'
                    ).data ?? other.data,
                  keys: other.keys,
                  request: other.request,
                },
              ]
            : []
        );
        entry = {
          keys,
          request,
          subscriptions: new Map(),
          status: {kind: 'idle'},
          data: null,
          error: null,
          lastUpdated: Date.now(),
          placeholder: findPlaceholder(settled, {keys, request}),
          releaseOrder: 0,
        };
        entries.set(keys.exact, entry);
        startEntryFetch(entry);
      } else if (entry.status.kind === 'stopped') {
        // Refresh from the partial data the stopped delta operation left behind.
        startEntryFetch(entry);
      }
      const subscribedEntry = entry;
      const token = Symbol('subscription');
      subscribedEntry.subscriptions.set(token, listener);
      listener(published(subscribedEntry));
      return () => release(subscribedEntry, token);
    },

    poll() {
      prune(true);
      // Kept levels hold data but do not fetch; one is refreshed by the first
      // poll after it is subscribed again.
      const now = Date.now();
      entries.forEach(entry => {
        if (
          entry.subscriptions.size > 0 &&
          entry.status.kind !== 'loading' &&
          now - entry.lastUpdated > POLL_STALE_MS
        ) {
          startEntryFetch(entry, 'poll');
        }
      });
    },
  };
}
